import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import router from '@/routes'
import { useClientStore } from '@/stores/ClientStore'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useConsent } from '@/vendors/consent'
import { whenever } from '@/utils/utils'

let sentryInstance = {}

export const createSentry = {
  install: (app, config = {}) => {
    const { getClient } = useClientStore()
    const { isVendorAllowed } = useConsent()
    const { authUser } = storeToRefs(useAuthenticationStore())

    const { client } = getClient()

    const { enabled = false } = config
    let sentry

    const isAllowed = computed(() => enabled && process.env.NODE_ENV !== 'development' && isVendorAllowed('sentry'))

    const loadSentry = async () => {
      try {
        sentry = await import('@sentry/browser')

        sentry.init({
          app,
          dsn: 'https://51db06cbf2e54792a6169cfb7be931a3@o190566.ingest.sentry.io/5502608',
          integrations: [
            sentry.browserTracingIntegration({ router })
          ],
          ignoreErrors: [
            'Non-Error exception captured',
            'Non-Error promise rejection captured'
          ],
          initialScope: { client: client.value.to_sentry },
          tracesSampleRate: 0.5,
          environment: process.env.NODE_ENV || 'production',
          beforeSend: event => isAllowed.value ? event : null
        })
      } catch (e) {
        throw e
      }
    }

    const message = (message, data = {}) => {
      try {
        let message_id

        sentry?.withScope(scope => {
          Object.keys(data).forEach(key => scope.setExtra(key, JSON.stringify(data[key])))

          if (authUser.value) scope.setUser({ id: authUser.value.id })

          message_id = sentry.captureMessage(message, 'info')
        })

        return message_id
      } catch (e) {
        throw e
      }
    }

    const error = (exception, data = {}) => {
      try {
        let error_id

        sentry?.withScope(scope => {
          Object.keys(data).forEach(key => scope.setExtra(key, JSON.stringify(data[key])))

          if (authUser.value) scope.setUser({ id: authUser.value.id })

          error_id = sentry.captureException(exception)
        })

        return error_id
      } catch (e) {
        throw e
      }
    }

    const withErrorHandler = async fn => {
      try {
        return await fn()
      } catch (e) {
        error(e)
      }
    }

    whenever(isAllowed, () => loadSentry(), { immediate: true, once: true })

    sentryInstance = {
      message,
      error,
      withErrorHandler
    }
  }
}

export const useSentry = () => sentryInstance